import axios from 'axios';
import { getSelectedTenantId } from './tenantStorage';
import { toast } from './toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    const tenantId = getSelectedTenantId();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized) and show toasts for other API errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      const loginPath = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/login` : '/login';
      window.location.href = loginPath;
      return Promise.reject(error);
    }
    if (status === 500) {
      toast.error('Server is experiencing high load or internal error. Please try again shortly.', {
        toastId: 'server-500-error', // Unique ID to prevent duplicates
      });
      return Promise.reject(error);
    }
    if (status && status >= 400) {
      const message = error.response?.data?.error || error.response?.data?.message || error.message || 'Request failed';
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  getAgentKey: () => api.post('/auth/agent-key'),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getOnboardingState: () => api.get('/auth/onboarding'),
  updateOnboardingState: (state) => api.post('/auth/onboarding', state),
};

export const scansAPI = {
  list: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return api.get(`/scans?${searchParams.toString()}`);
  },
  get: (scanId) => api.get(`/scans/${scanId}`),
  create: (data) => api.post('/scans', data),
  delete: (scanId) => api.delete(`/scans/${scanId}`),
  getFindings: (scanId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    return api.get(`/scans/${scanId}/findings?${params.toString()}`);
  },
  exportScan: (scanId, format = 'pdf') => {
    return api.get(`/scans/${scanId}/export?format=${format}`, {
      responseType: 'blob',
    });
  },
};

export const reportsAPI = {
  list: () => api.get('/reports'),
  get: (reportId) => api.get(`/reports/${reportId}`),
  generate: (formData) => api.post('/reports/generate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  download: (reportId, format = 'pdf') => api.get(`/reports/${reportId}/download?format=${format}`, {
    responseType: 'blob',
  }),
};

export const findingsAPI = {
  updateStatus: (findingId, status) => 
    api.patch(`/findings/${findingId}/status`, { status }),
};

export const networkAPI = {
  analyzeCapture: (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/network/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const idsAPI = {
  getStatus: () => api.get('/ids/status'),
  start: (data = {}) => api.post('/ids/start', data),
  stop: () => api.post('/ids/stop'),
};

export const statsAPI = {
  get: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return api.get(`/stats?${searchParams.toString()}`);
  },
};

export const utilsAPI = {
  ls: (path, useAgent = false, allowHubBrowse = false) => {
    const params = new URLSearchParams();
    params.append('path', path || '');
    params.append('use_agent', useAgent);
    if (allowHubBrowse) {
      params.append('allow_hub_browse', 'true');
    }
    return api.get(`/utils/ls?${params.toString()}`);
  },
  getSetupScript: (osType, command = false) => {
    const params = command ? '?command=true' : '';
    return api.get(`/utils/setup-script/${osType}${params}`);
  },
};

export const adminAPI = {
  listUsers: () => api.get('/admin/users'),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getTraffic: () => api.get('/admin/traffic'),
  testEmail: (email) => api.post('/scheduled-scans/test-email', email ? { email } : {}),
};

export const blogAPI = {
  list: () => api.get('/blog'),
  get: (postId) => api.get(`/blog/${postId}`),
  create: (data) => api.post('/blog', data),
  addComment: (postId, content) => api.post(`/blog/${postId}/comments`, { content }),
};

export const debugAPI = {
  getAgents: () => api.get('/debug/agents'),
};

export const tenantsAPI = {
  list: () => api.get('/msp/tenants'),
  create: (data) => api.post('/msp/tenants', data),
  delete: (tenantId) => api.delete(`/msp/tenants/${tenantId}`),
  get: (tenantId) => api.get(`/msp/tenants/${tenantId}`),
  getAssets: (tenantId) => api.get(`/msp/tenants/${tenantId}/assets`),
  getReports: (tenantId) => api.get(`/msp/tenants/${tenantId}/reports`),
  getReport: (tenantId, reportId) => api.get(`/msp/tenants/${tenantId}/reports/${reportId}`),
  getAlerts: (tenantId) => api.get(`/msp/tenants/${tenantId}/alerts`),
};

export const mspApi = {
  listTenants: () => api.get('/msp/tenants'),
  createTenant: (data) => api.post('/msp/tenants', data),
  deleteTenant: (tenantId) => api.delete(`/msp/tenants/${tenantId}`),
  getBranding: () => api.get('/msp/branding'),
  updateBranding: (data) => api.put('/msp/branding', data),
  uploadBrandLogo: (data) => api.post('/msp/branding/logo', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  triggerPortfolioScan: () => api.post('/msp/scans/external'),
  bulkUpdateTenants: (data) => api.post('/msp/tenants/bulk', data),
};

export const tenantApi = {
  getTenantDashboard: (tenantId) => api.get(`/tenants/${tenantId}/dashboard`),
  listAssets: (tenantId) => api.get(`/tenants/${tenantId}/assets`),
  listScans: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/scans`, { params }),
  triggerExternalScan: (tenantId, options = {}) =>
    api.post(`/tenants/${tenantId}/scans/external`, options),
  updateScanSchedule: (tenantId, data) => api.put(`/tenants/${tenantId}/scans/schedule`, data),
  getScanConfig: (tenantId) => api.get(`/tenants/${tenantId}/scans/config`),
  updateScanConfig: (tenantId, data) => api.put(`/tenants/${tenantId}/scans/config`, data),
  retryScan: (tenantId, scanId) => api.post(`/tenants/${tenantId}/scans/${scanId}/retry`),
  cancelScan: (tenantId, scanId) => api.post(`/tenants/${tenantId}/scans/${scanId}/cancel`),
  listFindings: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/findings`, { params }),
  updateFindingStatus: (tenantId, findingId, status) =>
    api.patch(`/tenants/${tenantId}/findings/${findingId}/status`, { status }),
  bulkUpdateFindings: (tenantId, data) => api.patch(`/tenants/${tenantId}/findings/bulk`, data),
  assignFinding: (tenantId, findingId, data) =>
    api.patch(`/tenants/${tenantId}/findings/${findingId}/assignee`, data),
  listAlerts: (tenantId) => api.get(`/tenants/${tenantId}/alerts`),
  acknowledgeAlert: (tenantId, alertId, data) =>
    api.post(`/tenants/${tenantId}/alerts/${alertId}/acknowledge`, data),
  listReports: (tenantId) => api.get(`/tenants/${tenantId}/reports`),
  generateReport: (tenantId) => api.post(`/tenants/${tenantId}/reports/generate`),
  updateReportSettings: (tenantId, data) =>
    api.put(`/tenants/${tenantId}/reports/settings`, data),
  deliverReport: (tenantId, reportId, data) =>
    api.post(`/tenants/${tenantId}/reports/${reportId}/deliver`, data),
  shareReport: (tenantId, reportId) =>
    api.post(`/tenants/${tenantId}/reports/${reportId}/share`),
  approveReport: (tenantId, reportId) =>
    api.post(`/tenants/${tenantId}/reports/${reportId}/approve`),
  exportReport: (tenantId, reportId, format = 'pdf') =>
    api.get(`/tenants/${tenantId}/reports/${reportId}/export?format=${format}`, {
      responseType: 'blob',
    }),
  getExternalScanDetail: (tenantId, scanId) =>
    api.get(`/tenants/${tenantId}/scans/${scanId}/detail`),
  setBaselineScan: (tenantId, scanId) =>
    api.post(`/tenants/${tenantId}/scans/${scanId}/set-baseline`),
  clearBaseline: (tenantId) =>
    api.delete(`/tenants/${tenantId}/scans/baseline`),
  getBaselineScan: (tenantId) =>
    api.get(`/tenants/${tenantId}/scans/baseline`),
  exportExternalScanReport: (tenantId, scanId, format = 'pdf') =>
    api.get(`/tenants/${tenantId}/scans/${scanId}/report?format=${format}`, {
      responseType: 'blob',
    }),
  updateAssetMetadata: (tenantId, assetId, data) =>
    api.put(`/tenants/${tenantId}/assets/${assetId}`, data),
  subscribeAssetMonitoring: (tenantId, data) =>
    api.post(`/tenants/${tenantId}/assets/subscribe`, data),
};

export const scheduledScansAPI = {
  list: () => api.get('/scheduled-scans'),
  get: (id) => api.get(`/scheduled-scans/${id}`),
  create: (data) => api.post('/scheduled-scans', data),
  update: (id, data) => api.put(`/scheduled-scans/${id}`, data),
  delete: (id) => api.delete(`/scheduled-scans/${id}`),
  runNow: (id) => api.post(`/scheduled-scans/${id}/run-now`),
  getHistory: (id) => api.get(`/scheduled-scans/${id}/history`),
};

export const alertsAPI = {
  list: (params = {}) => api.get('/alerts', { params }),
  acknowledge: (alertId) => api.post(`/alerts/${encodeURIComponent(alertId)}/acknowledge`),
};

export const changeDetectionAPI = {
  list: (scanId) => api.get(`/scans/${scanId}/changes`),
  listTenantTimeline: (tenantId) => api.get(`/tenants/${tenantId}/changes`),
};

export const discoveryScansAPI = {
  list: () => api.get('/discovery-scans'),
  get: (scanId) => api.get(`/discovery-scans/${scanId}`),
  create: (data) => api.post('/discovery-scans', data),
};

export const externalAssetsAPI = {
  list: () => api.get('/external-assets'),
  get: (assetId) => api.get(`/external-assets/${assetId}`),
};

export default api;
